
import { defineComponent, PropType } from "vue";
import * as apiClient from "@/apiclient/";
import common from "@/config/common";
import helper from "@/utils/helper";
import { mapGetters } from "vuex";

export default defineComponent({
  name: "ConfirmationOpenEnrollmentBenefit",
  props: {
    unbundledBenefit: {
      type: Object as PropType<apiClient.OpenEnrollmentBenefitDto>,
      default: null,
    },
    dependentData: {
      type: Array as () => apiClient.GetAllDependentDto[],
      default: [] as apiClient.GetAllDependentDto[],
    },
    bundle: {
      type: Object as PropType<apiClient.OpenEnrollmentBenefitsBundleDto>,
      default: null,
    },
  },
  data() {
    return {
      common,
      helper,
      effectiveDate: null as string | null,
      subsidyMessage: null as string | null,
      subsidyAmount: null as number | null,
    };
  },
  mounted() {
    this.effectiveDate = this.getEnrollment.openEnrollment.benefitEffectiveDate;
    this.getSubsidyInfo(this.unbundledBenefit, this.bundle);
  },
  methods: {
    tierName(
      unbundledBenefit: apiClient.OpenEnrollmentBenefitDto | null,
      bundle: apiClient.OpenEnrollmentBenefitsBundleDto | null
    ): string | null | undefined {
      if (!unbundledBenefit && !bundle) {
        return null;
      }

      var benefit = null as
        | apiClient.OpenEnrollmentBenefitDto
        | apiClient.OpenEnrollmentBenefitsBundleDto
        | null;
      if (unbundledBenefit) {
        benefit = unbundledBenefit;
      } else {
        benefit = bundle;
      }

      if (!benefit) {
        return null;
      }

      if (benefit.benefitRateTypeId === common.benefit.tier) {
        const selectedCoverage = benefit.tiers?.find(
          (option) => option.isSelected
        );

        if (selectedCoverage) {
          return selectedCoverage.tierLevelName;
        }
      } else if (benefit.benefitRateTypeId === common.benefit.single) {
        return "Single";
      } else if (benefit.benefitRateTypeId === common.benefit.unique) {
        return "Unique";
      }

      return null;
    },
    coverageDependentName(dependentId: number) {
      const dependent = this.dependentData.find(
        (person) => person.electionDependentId === dependentId
      );

      return dependent ? dependent.firstName + " " + dependent.lastName : "";
    },
    coverageDependentRelation(dependentId: number) {
      const dependent = this.dependentData.find(
        (person) => person.electionDependentId === dependentId
      );

      return dependent ? dependent.relationshipName : "";
    },
    formatEffectiveDate(date: string): string {
      if (!date) {
        return "";
      }

      const effectiveDate = new Date(date);
      effectiveDate.setDate(effectiveDate.getDate());
      return helper.formatDate(effectiveDate.toString());
    },
    monthlyPrice(
      unbundledBenefit: apiClient.OpenEnrollmentBenefitDto | null,
      bundle: apiClient.OpenEnrollmentBenefitsBundleDto | null
    ) {
      if (!unbundledBenefit && !bundle) {
        return null;
      }

      var benefit = null as
        | apiClient.OpenEnrollmentBenefitDto
        | apiClient.OpenEnrollmentBenefitsBundleDto
        | null;
      if (unbundledBenefit) {
        benefit = unbundledBenefit;
      } else {
        benefit = bundle;
      }

      if (!benefit) {
        return null;
      }

      if (benefit.benefitRateTypeId === common.benefit.tier) {
        const selectedCoverage = benefit.tiers!.find(
          (option) => option.isSelected
        );
        if (selectedCoverage) {
          return helper.formatNumber(selectedCoverage.monthlyPremiumRate!);
        }
      } else if (benefit.benefitRateTypeId === common.benefit.single) {
        return helper.formatNumber(benefit.singleMonthlyPremiumRate!);
      } else if (benefit.benefitRateTypeId === common.benefit.unique) {
        return helper.formatNumber(benefit.uniqueMonthlyPremiumRate ?? 0);
      }
    },
    getSubsidyInfo(
      unbundledBenefit: apiClient.OpenEnrollmentBenefitDto | null,
      bundle: apiClient.OpenEnrollmentBenefitsBundleDto | null
    ) {
      if (!unbundledBenefit && !bundle) {
        return;
      }

      var areDatesDifferent = false;
      if (unbundledBenefit) {
        if (
          unbundledBenefit.benefitRateTypeId == common.benefit.single ||
          unbundledBenefit.benefitRateTypeId == common.benefit.unique
        ) {
          const initialValue = 0;
          this.subsidyAmount = unbundledBenefit.subsidies!.reduce(
            (accumulator, currentItem) =>
              accumulator + (currentItem.amount ?? 0),
            initialValue
          );

          areDatesDifferent =
            unbundledBenefit.subsidies?.filter((subsidy) => {
              return unbundledBenefit.subsidies![0].endDate == subsidy.endDate;
            }).length != unbundledBenefit.subsidies?.length;

          this.setSubsidyMessage(areDatesDifferent);
        } else if (unbundledBenefit.benefitRateTypeId == common.benefit.tier) {
          var selectedCoverage = unbundledBenefit.tiers?.find(
            (tier) => tier.isSelected == true
          );

          if (selectedCoverage && selectedCoverage.subsidies) {
            const initialValue = 0;
            this.subsidyAmount = selectedCoverage.subsidies!.reduce(
              (accumulator, currentItem) =>
                accumulator + (currentItem.amount ?? 0),
              initialValue
            );

            areDatesDifferent =
              selectedCoverage.subsidies.filter((subsidy) => {
                return (
                  selectedCoverage!.subsidies![0].endDate == subsidy.endDate
                );
              }).length != selectedCoverage.subsidies.length;

            this.setSubsidyMessage(areDatesDifferent);
          }
        }
      }

      if (bundle) {
        if (bundle.benefitRateTypeId === common.benefit.tier) {
          selectedCoverage = bundle.tiers?.find(
            (tier) => tier.isSelected == true
          );

          if (selectedCoverage) {
            const initialValue = 0;
            this.subsidyAmount = selectedCoverage.subsidies!.reduce(
              (accumulator, currentItem) =>
                accumulator + (currentItem.amount ?? 0),
              initialValue
            );

            areDatesDifferent =
              selectedCoverage.subsidies?.filter((subsidy) => {
                return (
                  selectedCoverage!.subsidies![0].endDate == subsidy.endDate
                );
              }).length != selectedCoverage.subsidies?.length;

            this.setSubsidyMessage(areDatesDifferent);
          }
        } else if (
          bundle.benefitRateTypeId == common.benefit.single ||
          bundle.benefitRateTypeId == common.benefit.unique
        ) {
          var subsidyArray = [] as apiClient.SubsidyDto[];

          bundle.benefits?.forEach((benefit) => {
            if (benefit.subsidies) {
              subsidyArray = subsidyArray.concat(benefit.subsidies);
            }
          });

          if (subsidyArray.length) {
            const initialValue = 0;
            this.subsidyAmount = subsidyArray.reduce(
              (accumulator, currentItem) =>
                accumulator + (currentItem.amount ?? 0),
              initialValue
            );

            areDatesDifferent =
              subsidyArray.filter((subsidy) => {
                return subsidyArray[0].endDate == subsidy.endDate;
              }).length != subsidyArray.length;

            this.setSubsidyMessage(areDatesDifferent);
          }
        }
      }
    },
    setSubsidyMessage(areDatesDifferent: boolean) {
      if (this.subsidyAmount == 0 || this.subsidyAmount == null) {
        return;
      }

      var endOfMessage = "" as string | null | undefined;
      endOfMessage += areDatesDifferent ? "Multiple end dates may apply." : "";
      var amountString =
        this.subsidyAmount < 0
          ? "-$" + (this.subsidyAmount * -1).toFixed(2)
          : "$" + this.subsidyAmount.toFixed(2);

      this.subsidyMessage =
        "*Monthly premium includes an employer subsidy total of " +
        amountString +
        ". " +
        endOfMessage;
    },
    getDependentInformation(
      unbundledBenefit: apiClient.OpenEnrollmentBenefitDto | null,
      bundle: apiClient.OpenEnrollmentBenefitsBundleDto | null
    ): apiClient.GetAllDependentDto[] {
      if (!unbundledBenefit && !bundle) {
        return [] as apiClient.GetAllDependentDto[];
      }

      var electionDependentIds = null as number[] | null | undefined;
      if (unbundledBenefit) {
        electionDependentIds = unbundledBenefit.electionDependentIds;
      } else {
        electionDependentIds = bundle!.benefits![0].electionDependentIds;
      }

      if (!electionDependentIds) {
        return [] as apiClient.GetAllDependentDto[];
      }

      return this.dependentData.filter((dependent) => {
        return electionDependentIds?.includes(dependent.electionDependentId!);
      });
    },
  },
  computed: {
    ...mapGetters(["getEnrollment"]),
  },
});
